import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  InputLabel,
  FormControl,
  FormLabel,
  FormControlLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';

// COMPONENTS
import CustomModal from '../../components/common/CustomModal';
import TemplatesIcon from '../../components/icons/TemplatesIcon';
import Layout from '../../components/layouts/Layout';
import SingleView from '../../components/templates/SingleView';
import TemplateTable from '../../components/templates/TemplateTable';
import Loader from '../../components/common/Loader';

// MESSAGES
import TemplateFormattedMessages from '../../messages/websms/TemplateFormattedMessages';

// REQUESTS
import useTemplateRequest from '../../requests/TemplateRequest';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// UTILS
import {
  getCharacterCount,
  getMessageMaxLength,
  getMessageParts,
  withinCharacterLimit,
} from '../../utils/characterCount';

const Templates = ({
  customer,
  errorHandler,
  accessLevel,
  accessType,
  navigate,
}) => {
  const [stv, setStv] = useState(false);
  const [tab, setTab] = useState('Global');
  const [data, setData] = useState({ global: [], personal: [] });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [displayTemplatePopup, setDisplayTemplatePopup] = useState(false);
  const [globalListLength, setGlobalListLength] = useState(null);
  const [personalListLength, setPersonalListLength] = useState(null);
  const [templateData, setTemplateData] = useState({
    template_sms_id: null, // Integer ID, initialized to null
    template_name: '', // String for the template name
    template_text: '', // String for the template content/text
    template_type: '', // String for the template type
  });
  const [modalType, setModalType] = useState('');
  const [errorText, setErrorText] = useState('');
  const { getTemplates, createTemplate, editTemplate, deleteTemplate } =
    useTemplateRequest();

  const {
    standardCharacterOnly,
    unicodeCharacterOnly,
    characterCount,
    isUnicodeMessage,
  } = getCharacterCount(templateData?.template_text);

  const calculateCreditsAndRecipients = (msgValue) => {
    let messagePartCount = getMessageParts(msgValue);

    return {
      messagePartCount: messagePartCount,
    };
  };

  const { messagePartCount } = calculateCreditsAndRecipients(
    templateData?.template_text
  );

  const messageWithinLength = withinCharacterLimit(
    templateData?.template_text,
    isUnicodeMessage
  );
  const messageMaxLength = getMessageMaxLength(isUnicodeMessage);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (customer) {
        let fetchedData = await getTemplates(errorHandler);
        setData({
          global: sortList(fetchedData.global),
          personal: sortList(fetchedData.personal),
        });
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [customer, errorHandler]);

  const sortList = (list) =>
    list.sort((a, b) => a.template_name.localeCompare(b.template_name));

  const handleSelect = (template) => {
    setStv(true);
    setSelected(template);
  };

  const handleBackClick = () => {
    setStv(false);
    setSelected(null);
  };

  const handleToggleTemplateModal = () => {
    setDisplayTemplatePopup(!displayTemplatePopup);
    setErrorText(null);
    setModalType('');
  };

  useEffect(() => {
    if (data) {
      setGlobalListLength(data.global.length);
      setPersonalListLength(data.personal.length);
    }
  }, [data]);

  const handleSaveClick = async () => {
    if (
      templateData.template_name == '' ||
      templateData.template_text == '' ||
      templateData.template_type == ''
    ) {
      return setErrorText('Please fill out all fields');
    } else {
      setErrorText(null);
    }
    if (!saveDisabled) {
      setSaveDisabled(true);
      await createTemplate(
        templateData.template_name,
        templateData.template_text,
        templateData.template_type,
        errorHandler
      );
      const updatedData = await getTemplates(errorHandler);
      setData(updatedData);
      setSaveDisabled(false);
      setSelected(null);
      setDisplayTemplatePopup(false);
      setTemplateData({
        template_sms_id: null,
        template_name: '',
        template_text: '',
        template_type: '',
      });
    }
  };

  const handleEditClick = async () => {
    if (
      templateData.template_name == '' ||
      templateData.template_text == '' ||
      templateData.template_type == ''
    ) {
      return setErrorText('Please fill out all fields');
    } else {
      setErrorText(null);
    }
    if (!saveDisabled) {
      setSaveDisabled(true);
      const updates = {
        template_type: templateData.template_type,
        template_name: templateData.template_name,
        template_text: templateData.template_text,
      };
      await editTemplate(templateData.template_sms_id, updates, errorHandler);
      const updatedData = await getTemplates(errorHandler);
      setData(updatedData);
      setDisplayTemplatePopup(false);
      setSaveDisabled(false);
    }
  };

  const handleDeleteClick = async (templateId) => {
    await deleteTemplate(templateId, tab, errorHandler);
    const updatedData = await getTemplates(errorHandler);
    setData(updatedData);
    setSelected(null);
    setDisplayTemplatePopup(false);
    setTemplateData({
      template_sms_id: null,
      template_name: '',
      template_text: '',
      template_type: '',
    });
  };

  const handleAddNewClick = () => {
    setTemplateData({
      template_sms_id: null,
      template_name: '',
      template_text: '',
      template_type: '',
    });
    setModalType('add');
    setDisplayTemplatePopup(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTemplateData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSetTab = (event, newValue) => {
    setTab(newValue);
    setSelected(null);
  };

  const generateBody = () => {
    if (loading) return <Loader />;

    if (stv) {
      return (
        <SingleView
          accessLevel={accessLevel}
          accessType={accessType}
          tab={tab}
          selected={selected}
          callBack={handleBackClick}
          handleSave={handleSaveClick}
          saveDisabled={saveDisabled}
          handleEdit={handleEditClick}
          handleDelete={handleDeleteClick}
          errorHandler={errorHandler}
        />
      );
    }

    return (
      <Box sx={{ maxWidth: 900 }}>
        <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={handleSetTab}>
            <Tab
              icon={<TemplatesIcon />}
              label={
                <Stack
                  sx={{
                    minWidth: { md: 200 },
                    paddingLeft: { xs: 0, md: 1.5 },
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'end',
                  }}
                >
                  <Typography component="span">Global</Typography>
                  <Typography
                    component="span"
                    sx={{
                      display: { xs: 'none', md: 'inline-block' },
                    }}
                  >{`${globalListLength ?? 0} ${
                    globalListLength == 1 ? 'Template' : 'Templates'
                  }`}</Typography>
                </Stack>
              }
              value="Global"
            />
            <Tab
              icon={<TemplatesIcon />}
              label={
                <Stack
                  sx={{
                    minWidth: { md: 200 },
                    paddingLeft: { xs: 0, md: 1.5 },
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'end',
                  }}
                >
                  <Typography component="span">Personal</Typography>
                  <Typography
                    component="span"
                    sx={{
                      display: { xs: 'none', md: 'inline-block' },
                    }}
                  >{`${personalListLength ?? 0} ${
                    personalListLength == 1 ? 'Template' : 'Templates'
                  }`}</Typography>
                </Stack>
              }
              value="Personal"
            />
          </Tabs>
        </Box>

        <TemplateTable
          list={tab === 'Global' ? data.global : data.personal}
          errorHandler={errorHandler}
          handleAddNewClick={handleAddNewClick}
          setTemplateData={setTemplateData}
          setDisplayTemplatePopup={setDisplayTemplatePopup}
          type={tab}
          setData={setData}
          accessLevel={accessLevel}
          accessType={accessType}
          setModalType={setModalType}
        />
      </Box>
    );
  };

  return (
    <Layout
      routes={WebsmsRoutes}
      title={TemplateFormattedMessages.templateTitle}
    >
      {generateBody()}

      <CustomModal
        setOpen={setDisplayTemplatePopup}
        open={displayTemplatePopup}
        handleClose={handleToggleTemplateModal}
        title={
          modalType === 'add'
            ? TemplateFormattedMessages.templateAddTitle
            : TemplateFormattedMessages.templateEditTitle
        }
        width={550}
        icon="templates"
      >
        <Grid
          container
          sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
          spacing={2}
        >
          <Grid size={12}>
            <FormControl component="fieldset">
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <FormLabel
                  sx={{ minWidth: 150 }}
                  required
                  disabled={modalType !== 'add'}
                >
                  {TemplateFormattedMessages.contactType}
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="TemplateType"
                  name="template_type"
                  value={templateData?.template_type}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="Personal"
                    control={<Radio color="secondary" />}
                    label="Personal"
                    disabled={modalType === 'edit'}
                  />
                  <FormControlLabel
                    value="Global"
                    control={<Radio color="secondary" />}
                    label="Global"
                    disabled={
                      accessLevel <= '0' ||
                      accessType === '0' ||
                      modalType === 'edit'
                    }
                  />
                </RadioGroup>
              </Stack>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }} required>
                {TemplateFormattedMessages.nameText}
              </InputLabel>

              <TextField
                fullWidth
                onChange={handleInputChange}
                name="template_name"
                value={templateData?.template_name}
                size="small"
                required
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack>
              <InputLabel required>
                {TemplateFormattedMessages.contentText}
              </InputLabel>

              <TextField
                size="small"
                name="template_text"
                value={templateData?.template_text}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                required
              />

              <Typography
                fontSize={11}
                textAlign="right"
                sx={{
                  maxWidth: 800,
                  marginTop: 0.5,
                }}
              >
                <Link
                  tabIndex={0}
                  onClick={() => {
                    navigate('/websms/bulk-send', {
                      state: { landingMessage: true },
                    });
                  }}
                  variant="grey"
                >
                  {TemplateFormattedMessages.viewGuide}
                </Link>
              </Typography>
            </Stack>

            <Box sx={{ padding: 2 }}>
              <Typography
                fontSize={12}
                sx={{
                  color: !messageWithinLength ? 'error.main' : 'inherit',
                  fontWeight: !messageWithinLength ? 700 : 400,
                }}
              >
                <strong>{`${characterCount}/${messageMaxLength} Character(s)`}</strong>
                {` = ${standardCharacterOnly} Standard Character(s)
                        ${
                          unicodeCharacterOnly > 0
                            ? ' + ' +
                              unicodeCharacterOnly +
                              ' Unicode Character(s)'
                            : ''
                        }`}
              </Typography>

              <Typography fontSize={12}>
                <strong>{`${messagePartCount} Message Parts`}</strong>
              </Typography>

              {errorText && (
                <Alert sx={{ marginTop: 1 }} severity="error">
                  {errorText}
                </Alert>
              )}

              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  marginTop: 2,
                  gap: 2,
                }}
              >
                {templateData.template_sms_id == null ? (
                  <Button
                    disabled={saveDisabled}
                    onClick={handleSaveClick}
                    variant="contained"
                    color="secondary"
                  >
                    create template
                  </Button>
                ) : (
                  <>
                    <Button
                      color="secondary"
                      onClick={handleToggleTemplateModal}
                      variant="link"
                    >
                      {TemplateFormattedMessages.cancelText}
                    </Button>
                    <Button
                      onClick={handleEditClick}
                      disabled={saveDisabled}
                      variant="contained"
                      color="secondary"
                    >
                      {TemplateFormattedMessages.saveText}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </CustomModal>
    </Layout>
  );
};

export default Templates;
